/**
 * Permissions Utilities
 * 
 * Frontend permission matrix and helper functions for role-based access control.
 * Mirrors the backend permissions module for consistent enforcement.
 * 
 * @module lib/permissions
 */

// =============================================================================
// TYPES
// =============================================================================

/**
 * Business-level roles.
 * firm_owner is the sole "god mode" role at the firm level with full access.
 */
export type AppRole = 'firm_owner' | 'admin' | 'attorney' | 'paralegal' | 'support';
export type OrgRole = 'owner' | 'admin' | 'member';
export type PlatformRole = 'super_admin';

export type Resource = 
  | 'matters' 
  | 'clients' 
  | 'courts'
  | 'regions'
  | 'businesses'
  | 'pipelines' 
  | 'stages'
  | 'processes'
  | 'templates'
  | 'workspaces'
  | 'tasks' 
  | 'documents' 
  | 'billing' 
  | 'staff' 
  | 'audit';

export type Action = 
  | 'create' 
  | 'read' 
  | 'update' 
  | 'delete' 
  | 'invite' 
  | 'change_role' 
  | 'remove';

// =============================================================================
// PERMISSION MATRIX
// =============================================================================

/**
 * Permission matrix defining what actions each role can perform on each resource.
 * This must stay in sync with the backend permissions module.
 * 
 * firm_owner is the sole "god mode" role at the firm level with full access.
 */
export const permissionMatrix: Record<AppRole, Partial<Record<Resource, Action[]>>> = {
  // Firm owners have full access to all resources in their firm (implicit org owner access)
  firm_owner: {
    matters: ['create', 'read', 'update', 'delete'],
    clients: ['create', 'read', 'update', 'delete'],
    courts: ['create', 'read', 'update', 'delete'],
    regions: ['create', 'read', 'update', 'delete'],
    businesses: ['create', 'read', 'update', 'delete'],
    pipelines: ['create', 'read', 'update', 'delete'],
    stages: ['create', 'read', 'update', 'delete'],
    processes: ['create', 'read', 'update', 'delete'],
    templates: ['create', 'read', 'update', 'delete'],
    workspaces: ['create', 'read', 'update', 'delete'],
    tasks: ['create', 'read', 'update', 'delete'],
    documents: ['create', 'read', 'update', 'delete'],
    billing: ['create', 'read', 'update', 'delete'],
    staff: ['invite', 'read', 'change_role', 'remove'],
    audit: ['read'],
  },
  // admin is an alias for firm_owner (backward compatibility - kept in enum but use firm_owner for new code)
  admin: {
    matters: ['create', 'read', 'update', 'delete'],
    clients: ['create', 'read', 'update', 'delete'],
    courts: ['create', 'read', 'update', 'delete'],
    regions: ['create', 'read', 'update', 'delete'],
    businesses: ['create', 'read', 'update', 'delete'],
    pipelines: ['create', 'read', 'update', 'delete'],
    stages: ['create', 'read', 'update', 'delete'],
    processes: ['create', 'read', 'update', 'delete'],
    templates: ['create', 'read', 'update', 'delete'],
    workspaces: ['create', 'read', 'update', 'delete'],
    tasks: ['create', 'read', 'update', 'delete'],
    documents: ['create', 'read', 'update', 'delete'],
    billing: ['create', 'read', 'update', 'delete'],
    staff: ['invite', 'read', 'change_role', 'remove'],
    audit: ['read'],
  },
  attorney: {
    matters: ['create', 'read', 'update'],
    clients: ['create', 'read', 'update'],
    courts: ['read'],
    regions: ['read'],
    businesses: ['read'],
    pipelines: ['read'],
    stages: ['read'],
    processes: ['create', 'read', 'update'],
    templates: ['read', 'update'],
    workspaces: ['read'],
    tasks: ['create', 'read', 'update'],
    documents: ['create', 'read', 'update'],
    billing: ['read'],
    staff: ['read'],
    audit: [],
  },
  paralegal: {
    matters: ['read', 'update'],
    clients: ['read', 'update'],
    courts: ['read'],
    regions: ['read'],
    businesses: ['read'],
    pipelines: ['read'],
    stages: ['read'],
    processes: ['read', 'update'],
    templates: ['read'],
    workspaces: ['read'],
    tasks: ['create', 'read', 'update'],
    documents: ['create', 'read', 'update'],
    billing: ['read'],
    staff: ['read'],
    audit: [],
  },
  support: {
    matters: ['read'],
    clients: ['read'],
    courts: ['read'],
    regions: ['read'],
    businesses: ['read'],
    pipelines: ['read'],
    stages: ['read'],
    processes: ['read'],
    templates: ['read'],
    workspaces: ['read'],
    tasks: ['read'],
    documents: ['read'],
    billing: ['read'],
    staff: ['read'],
    audit: [],
  },
};

// =============================================================================
// PERMISSION CHECK FUNCTIONS
// =============================================================================

/**
 * Check if a role can perform an action on a resource
 */
export function canPerform(
  role: AppRole | null,
  resource: Resource,
  action: Action
): boolean {
  if (!role) return false;
  const resourcePermissions = permissionMatrix[role]?.[resource];
  return resourcePermissions?.includes(action) ?? false;
}

/**
 * Get all allowed actions for a role on a resource
 */
export function getAllowedActions(role: AppRole | null, resource: Resource): Action[] {
  if (!role) return [];
  return permissionMatrix[role]?.[resource] ?? [];
}

/**
 * Check if a role has any access to a resource
 */
export function hasResourceAccess(role: AppRole | null, resource: Resource): boolean {
  if (!role) return false;
  const actions = permissionMatrix[role]?.[resource];
  return actions !== undefined && actions.length > 0;
}

/**
 * Get a user-friendly description of why an action is not allowed
 */
export function getPermissionDeniedReason(resource: Resource, action: Action): string {
  const actionDescriptions: Record<Action, string> = {
    create: 'create',
    read: 'view',
    update: 'edit',
    delete: 'delete',
    invite: 'invite members to',
    change_role: 'change roles in',
    remove: 'remove members from',
  };
  
  const resourceDescriptions: Record<Resource, string> = {
    matters: 'matters',
    clients: 'clients',
    courts: 'courts',
    regions: 'regions',
    businesses: 'businesses',
    pipelines: 'pipelines',
    stages: 'stages',
    processes: 'processes',
    templates: 'templates',
    workspaces: 'workspaces',
    tasks: 'tasks',
    documents: 'documents',
    billing: 'billing',
    staff: 'team members',
    audit: 'audit logs',
  };
  
  return `Only Admins (Firm Owners) can ${actionDescriptions[action]} ${resourceDescriptions[resource]}`;
}

/**
 * Get permission denied reason with "request access" hint
 */
export function getPermissionDeniedReasonWithHint(
  resource: Resource, 
  action: Action,
  showHint: boolean = true
): string {
  const baseReason = getPermissionDeniedReason(resource, action);
  
  if (!showHint) return baseReason;
  
  return `${baseReason}. Request access from your Firm Owner.`;
}

// =============================================================================
// ROLE DESCRIPTIONS (for tooltips)
// =============================================================================

/**
 * Human-readable descriptions of what each role can do
 */
export const roleDescriptions: Record<string, string> = {
  super_admin: 'Full platform access. Can manage all firms, businesses, and users.',
  owner: 'Full access to firm settings, businesses, and team management.',
  member: 'Standard organization member. Access determined by business-level role.',
  firm_owner: 'Full access to all businesses in the firm. Can manage settings, members, and all features.',
  attorney: 'Can manage cases, clients, and templates. Limited team access.',
  paralegal: 'Can view and edit cases. Limited template and settings access.',
  support: 'Read-only access to most features.',
};

/**
 * Get role display name
 */
export function getRoleDisplayName(role: AppRole | OrgRole | PlatformRole | null): string {
  if (!role) return 'No Role';
  
  const displayNames: Record<string, string> = {
    super_admin: 'Super Admin',
    owner: 'Organization Owner',
    firm_owner: 'Firm Owner',
    attorney: 'Attorney',
    paralegal: 'Paralegal',
    support: 'Support',
  };
  
  return displayNames[role] ?? role;
}

/**
 * Get role badge color class
 */
export function getRoleBadgeColor(role: AppRole | OrgRole | PlatformRole | null): string {
  if (!role) return 'bg-muted text-muted-foreground';
  
  const colors: Record<string, string> = {
    super_admin: 'bg-destructive text-destructive-foreground',
    owner: 'bg-primary text-primary-foreground',
    firm_owner: 'bg-primary text-primary-foreground',
    attorney: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    paralegal: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200',
    support: 'bg-muted text-muted-foreground',
  };
  
  return colors[role] ?? 'bg-muted text-muted-foreground';
}
